/**
 * Centralized metadata for major cryptocurrencies (BTC, ETH)
 * These are non-Abstract blockchain tokens that we track alongside Abstract tokens
 * 
 * Usage: Import this service anywhere you need consistent BTC/ETH metadata
 */

export interface MajorCryptoMetadata {
  symbol: string;
  name: string;
  slug: string;
  imageUrl: string;
  fallbackImageUrl?: string; // Backup if primary fails
  description: string;
  blockchain: string;
}

export const MAJOR_CRYPTO_METADATA: Record<string, MajorCryptoMetadata> = {
  BTC: {
    symbol: 'BTC',
    name: 'Bitcoin',
    slug: 'btc',
    imageUrl: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png',
    fallbackImageUrl: 'https://cryptologos.cc/logos/bitcoin-btc-logo.png',
    description: 'The first and largest cryptocurrency. Bitcoin (BTC) is a decentralized digital asset and store of value powered by blockchain technology.',
    blockchain: 'Bitcoin',
  },
  ETH: {
    symbol: 'ETH',
    name: 'Ethereum',
    slug: 'eth',
    imageUrl: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png',
    fallbackImageUrl: 'https://cryptologos.cc/logos/ethereum-eth-logo.png',
    description: 'Ethereum is a decentralized platform that runs smart contracts and is the foundation for thousands of tokens and decentralized applications.',
    blockchain: 'Ethereum',
  },
};

/**
 * Check if a symbol is a major crypto (BTC or ETH)
 */
export function isMajorCrypto(symbol: string): boolean {
  const symbolUpper = symbol.toUpperCase();
  return symbolUpper === 'BTC' || symbolUpper === 'ETH';
}

/**
 * Get metadata for a major crypto by symbol
 */
export function getMajorCryptoMetadata(symbol: string): MajorCryptoMetadata | null {
  const symbolUpper = symbol.toUpperCase();
  return MAJOR_CRYPTO_METADATA[symbolUpper] || null;
}

/**
 * Get fallback asset metadata for tag pages
 * Used when asset_metadata collection doesn't have an entry
 */
export function getMajorCryptoAssetMetadata(symbol: string) {
  const meta = getMajorCryptoMetadata(symbol);
  if (!meta) return null;

  return {
    id: symbol.toLowerCase(),
    assetType: 'token' as const,
    name: meta.name,
    symbol: meta.symbol,
    slug: meta.slug,
    imageUrl: meta.imageUrl,
    description: meta.description,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Get image URL with fallback
 */
export function getMajorCryptoImageUrl(symbol: string): string | null {
  const meta = getMajorCryptoMetadata(symbol);
  return meta?.imageUrl || null;
}

/**
 * Get all major crypto symbols
 */
export function getAllMajorCryptoSymbols(): string[] {
  return Object.keys(MAJOR_CRYPTO_METADATA);
}

